import React, { Fragment, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Container, Row, Card, CardBody, CardHeader, Collapse, Form } from 'reactstrap';
import TableBuilder, { ApiRequest, ModalButtonBuilder, ButtonBuilder, HoverOver, RegisteredInputsBuilder, VerticalButtonGroup, ToastBuilder, LabelWithSubtext, CheckBox } from '../../../../../../../../../GlobalComponents/Builders';
import { buildOptionsFromArray, buildSelectOptionsFromArray, setInitialFormValues, sortByKey, handleCopy } from '../../../../../../../../../GlobalComponents/Helpers';
import { Btn } from '../../../../../../../../../AbstractElements';
import { Col } from 'react-bootstrap';
import { ProgressLoader } from '../../../../../../../../../Layout/Loader';
import { ArrowRight } from 'react-feather';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import ImportAssociationMappingsModal from './ImportAssociationMappingsModal';
import MapByLeafModal from './MapByLeafModal';
import BulkEditControls from '../../shared/BulkEditControls';
import CompositeValueSelect from '../composite/CompositeValueSelect';
import useConfirmModal from '../../../../../../../../../GlobalComponents/Builders/ConfirmModal';
import {
    INACTIVE_STATUSES, buildConflictIndex as buildSharedConflictIndex, getConflicts as getSharedConflicts, conflictGroupKey,
    getConflictGroups, buildTagMap, ConflictTag, ConflictTagWithInfo, OverloadLegend,
} from '../../shared/mappingConflicts';

// '0' marks an unused level on synthetic type/subtype-level association rows.
const hasLevel = (id) => id !== undefined && id !== null && String(id) !== '0' && String(id) !== '';

const getSourceLevel = (row) => row.item_name ? 'Item' : row.subtype_name ? 'SubType' : 'Type';

// A TSI leaf's parent context (board, then type, then subtype). Reverse sync filters on this context and must land on one leaf.
const getLeafContext = (row) => {
    if (row.item_name) return {
        key: [row.board_id ?? row.board_name, row.type_value_id ?? row.type_name, row.subtype_value_id ?? row.subtype_name].join('|'),
        label: `${row.board_name} / ${row.type_name} / ${row.subtype_name}`,
        leaf: row.item_name,
    };
    if (row.subtype_name) return {
        key: [row.board_id ?? row.board_name, row.type_value_id ?? row.type_name].join('|'),
        label: `${row.board_name} / ${row.type_name}`,
        leaf: row.subtype_name,
    };
    return { key: String(row.board_id ?? row.board_name), label: row.board_name || '', leaf: row.type_name };
};

// Composite adapter for the shared conflict util.
const CONFLICT_ADAPTER = { getContext: getLeafContext, getId: (r) => r.mapping_id, getDestination: (r) => r.destination_id };
const buildConflictIndex = (rows, overrides = {}) => buildSharedConflictIndex(rows, CONFLICT_ADAPTER, overrides);
const getConflicts = (index, row, destId) => getSharedConflicts(index, row, destId, CONFLICT_ADAPTER);
const getGroupKey = (row, destId) => conflictGroupKey(destId, row, getLeafContext);

const OverloadWarning = ({ row, conflicts, destLabel, tag }) => conflicts.length === 0 ? null : (
    <div className='p-2 mb-2' style={{ border: '1px solid #ffe8a1', borderRadius: 6, background: '#fff8e1', fontSize: 13 }}>
        <div className='txt-warning d-flex align-items-center' style={{ fontWeight: 600, gap: 6 }}>Overloaded destination {tag ? <ConflictTag tag={tag} /> : null}</div>
        <div>
            {destLabel ? <strong>{destLabel}</strong> : 'This destination'} is also mapped from {conflicts.length === 1 ? 'another leaf' : `${conflicts.length} other leaves`} in <strong>{getLeafContext(row).label}</strong>:
        </div>
        <ul className='mb-1' style={{ paddingLeft: 20 }}>
            {conflicts.map(c => <li key={c.mapping_id}>{getLeafContext(c).leaf} <span className='text-muted'>({c.status})</span></li>)}
        </ul>
        <small className='text-muted'>Reverse sync can't tell these apart within the same board/type. Map each leaf to its own destination, or ignore the extras.</small>
    </div>
);

const SourceDetail = ({ label, name, id }) => (
    <div className='d-flex justify-content-between' style={{ gap: 10, fontSize: 13, lineHeight: 1.6 }}>
        <span className='text-muted' style={{ minWidth: 70 }}>{label}</span>
        <span className='text-end' style={{ flex: 1 }}>{name || '-'}{hasLevel(id) ? <span className='text-muted' style={{ fontSize: 11 }}> ({id})</span> : null}</span>
    </div>
);

/**
 * AssociationMappingSection — Bidirectional composite chain mapper.
 *
 * Follows the same pattern as ValueMappingSection:
 * - Card with accordion header
 * - TableBuilder with columns
 * - ModalButtonBuilder for Edit
 * - VerticalButtonGroup for actions
 */
const AssociationMappingSection = ({ jobData, jobResourceMappings }) => {

    const [isOpen, setIsOpen] = useState(null);
    const [mappings, setMappings] = useState({});  // keyed by sourceResourceId
    const [summary, setSummary] = useState({});    // keyed by source_type: { total_count, approved_count, mapped_count }
    const [destLeaves, setDestLeaves] = useState([]);
    const [destChainData, setDestChainData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadedGroups, setLoadedGroups] = useState({});

    const { register, handleSubmit, control, setValue, watch, formState: { errors } } = useForm();
    const { confirm, ConfirmModal } = useConfirmModal();
    const actionForm = useForm();
    const { register: actionRegister, handleSubmit: actionHandleSubmit, control: actionControl, setValue: actionSetValue, reset: actionReset, watch: actionWatch, formState: { errors: actionErrors } } = actionForm;
    const bulkDestinationId = actionWatch('bulkDestinationId');

    // Bulk edit state
    const [bulkEditMode, setBulkEditMode] = useState(false);
    const bulkSelectRef = useRef({});
    const lastClickedIndex = useRef({});
    const sortedTableDataRef = useRef({});
    const [bulkSelectVersion, setBulkSelectVersion] = useState(0);
    const bumpBulkSelect = () => setBulkSelectVersion(v => v + 1);
    const getBulkSet = (resourceId) => bulkSelectRef.current[resourceId] || new Set();
    const getBulkCount = (resourceId) => getBulkSet(resourceId).size;

    // Import log (in-memory, accumulates until page refresh)
    const [importLog, setImportLog] = useState([]);
    const [lastImportSettings, setLastImportSettings] = useState(null);
    const [reloadBanner, setReloadBanner] = useState(null);
    const [mappingFilter, setMappingFilter] = useState('active');
    const [levelFilter, setLevelFilter] = useState('all');
    const [boardFilter, setBoardFilter] = useState('all');
    const [conflictsOnly, setConflictsOnly] = useState(false);
    const [tagFilter, setTagFilter] = useState(null);

    const [loaded, setLoaded] = useState(false);

    const jobId = jobData?.id;

    // Derive composite mapping entries from jobResourceMappings
    // Also includes sync job's association-type composites (bidirectional mappings shared across paired jobs)
    const [syncCompositeEntries, setSyncCompositeEntries] = useState([]);

    const compositeEntries = useMemo(() => {
        const local = jobResourceMappings?.current?.filter(m => m.mapping_type === 'composite' && m.source_resource_id === 69) || [];
        // Merge with sync job association composites (avoid duplicates by source_resource_id + destination_resource_id)
        const localKeys = new Set(local.map(m => `${m.source_resource_id}-${m.destination_resource_id}`));
        const merged = [...local];
        for (const entry of syncCompositeEntries) {
            if (!localKeys.has(`${entry.source_resource_id}-${entry.destination_resource_id}`)) {
                merged.push(entry);
            }
        }
        return merged;
    }, [jobResourceMappings?.current, syncCompositeEntries]);

    // Fetch sync job's association-type composite mappings (if sync job exists)
    useEffect(() => {
        const syncJobId = jobData?.sync_job_id;
        if (!syncJobId || syncJobId <= 0) return;

        ApiRequest('GET', '/dmt/jobs/{jobId}/mappings', {
            pathParams: { jobId: syncJobId },
        }).then(res => {
            if (!res.success || !res.data) return;
            // Only include composites where at least one chain is association_table type
            const assocComposites = res.data.filter(m => {
                if (m.mapping_type !== 'composite') return false;
                const options = typeof m.mapping_options === 'string'
                    ? JSON.parse(m.mapping_options || '{}')
                    : (m.mapping_options || {});
                const srcType = options?.source_chain?.type;
                const dstType = options?.destination_chain?.type;
                return srcType === 'association_table' || dstType === 'association_table';
            });
            setSyncCompositeEntries(assocComposites);
        });
    }, [jobData?.sync_job_id]);

    // Fetch summary on mount — independent of compositeEntries
    useEffect(() => {
        if (!jobId) return;
        ApiRequest('GET', '/dmt/jobs/{jobId}/compositeMappings', {
            pathParams: { jobId },
            queryParams: { summary: 'true' },
        }).then(res => {
            if (res.success) {
                const data = res.data || [];
                const lookup = {};
                (Array.isArray(data) ? data : []).forEach(s => { lookup[s.source_type] = s; });
                setSummary(lookup);
            }
        });
    }, [jobId]);

    const fetchGroupData = useCallback(async (sourceResourceId) => {
        if (!jobId) return;
        setLoading(true);

        const [mappingsRes, destRes] = await Promise.all([
            ApiRequest('GET', '/dmt/jobs/{jobId}/compositeMappings', {
                pathParams: { jobId },
                queryParams: { sourceResourceId },
            }),
            // Only fetch dest tree once
            destLeaves.length === 0
                ? ApiRequest('GET', '/dmt/jobs/{jobId}/valuemappings/chainOptions', {
                    pathParams: { jobId },
                    queryParams: { sourceResourceId, side: 'destination' },
                })
                : Promise.resolve(null),
        ]);

        if (mappingsRes.success) {
            const data = mappingsRes.data || [];
            const rows = Array.isArray(data) ? data : (data?.records || []);
            setMappings(prev => ({ ...prev, [sourceResourceId]: rows }));
        }

        if (destRes && destRes.success) {
            let data = destRes.data?.records || destRes.data;
            if (typeof data === 'string') try { data = JSON.parse(data); } catch(e) { data = null; }
            if (data) {
                const leaves = [];
                const walk = (nodes, path = []) => {
                    for (const node of (nodes || [])) {
                        const currentPath = [...path, node.value];
                        leaves.push({ ...node, path: currentPath, pathLabel: currentPath.join(' > ') });
                        if (node.children && node.children.length > 0) walk(node.children, currentPath);
                    }
                };
                walk(data.options);
                setDestLeaves(leaves);
                setDestChainData(data);
            }
        }
        setLoading(false);
        setLoadedGroups(prev => ({ ...prev, [sourceResourceId]: true }));
    }, [jobId, destLeaves.length]);


    const handleMappingUpdate = async (formData, mappingId, row) => {
        const destValueField = formData['destinationResourceValueId' + mappingId];
        const statusField = formData['status' + mappingId];
        const notesField = formData['notes' + mappingId];

        const res = await ApiRequest('PATCH', '/dmt/jobs/{jobId}/compositeMappings/{mappingId}', {
            pathParams: { jobId, mappingId: String(mappingId) },
            body: {
                destination_id: destValueField || row.destination_id || 0,
                status: statusField || row.status,
                notes: notesField || null,
            },
        });

        if (res.success) {
            ToastBuilder('success', 'Mapping updated');
            // Refresh the group this mapping belongs to
            const sourceResourceId = compositeEntries.find(e => 
                (mappings[e.source_resource_id] || []).some(m => m.mapping_id === mappingId)
            )?.source_resource_id;
            if (sourceResourceId) fetchGroupData(sourceResourceId);
        } else {
            ToastBuilder('error', res.error || 'Failed to update');
        }
    };

    // Re-pull from the source API. 'reload-composite-dependencies' is the heavy one (both chains' values + TSI), so it confirms first.
    const repullFromApi = async (sourceResourceId, actionName) => {
        if (actionName === 'reload-composite-dependencies') {
            const confirmed = await confirm('Re-import both chains\u2019 values and the associations for this resource? This calls the source API and may take a minute.', {
                title: 'Re-pull values + associations',
                confirmLabel: 'Re-pull',
                cancelLabel: 'Cancel',
            });
            if (!confirmed) return;
        }
        await ApiRequest('POST', '/dmt/jobs/{jobId}/actions/{actionName}', {
            pathParams: { jobId, actionName },
            body: { sourceResourceId },
            toastOnError: true,
        });
        setReloadBanner(sourceResourceId);
        setTimeout(() => setReloadBanner(null), 30000);
    };

    const handleDeleteMapping = async (row, sourceResourceId) => {
        const confirmed = await confirm('Remove this composite mapping? It will be excluded from migrations. The source association is kept.', {
            title: 'Remove Composite Mapping',
            confirmLabel: 'Remove',
            cancelLabel: 'Cancel',
        });
        if (!confirmed) return;

        // Soft delete via status; keep destination and notes so the row can be restored from the Inactive filter.
        const res = await ApiRequest('PATCH', '/dmt/jobs/{jobId}/compositeMappings/{mappingId}', {
            pathParams: { jobId, mappingId: String(row.mapping_id) },
            body: { destination_id: row.destination_id > 0 ? row.destination_id : 0, status: 'Delete', notes: row.notes || null },
            toastOnError: true,
        });
        if (res.success) {
            ToastBuilder('success', 'Mapping deleted');
            if (sourceResourceId) fetchGroupData(sourceResourceId);
        }
    };

    const handleQuickApprove = async (sourceResourceId) => {
        const res = await ApiRequest('POST', '/dmt/jobs/{jobId}/actions/{actionName}', {
            pathParams: { jobId, actionName: 'quick-approve-composite' },
            body: { sourceResourceId },
        });
        if (res.success) {
            const count = res.data?.approved || 0;
            ToastBuilder('success', `${count} mapping(s) approved`);
        } else {
            ToastBuilder('error', res.data?.message || 'Failed to approve');
        }
        fetchGroupData(sourceResourceId);
    };

    // Bulk edit handlers
    const handleBulkCheckbox = (e, mappingId, resourceId) => {
        const currentData = sortedTableDataRef.current[resourceId] || [];
        const currentIndex = currentData.findIndex(row => row.mapping_id === mappingId);
        const group = new Set(getBulkSet(resourceId));

        if (e.nativeEvent.shiftKey && lastClickedIndex.current[resourceId] !== undefined && currentIndex >= 0) {
            const start = Math.min(lastClickedIndex.current[resourceId], currentIndex);
            const end = Math.max(lastClickedIndex.current[resourceId], currentIndex);
            const rangeIds = currentData.slice(start, end + 1).map(row => row.mapping_id);
            if (e.target.checked) {
                rangeIds.forEach(id => group.add(id));
            } else {
                rangeIds.forEach(id => group.delete(id));
            }
        } else {
            if (e.target.checked) group.add(mappingId); else group.delete(mappingId);
        }

        bulkSelectRef.current = { ...bulkSelectRef.current, [resourceId]: group };
        if (currentIndex >= 0) lastClickedIndex.current = { ...lastClickedIndex.current, [resourceId]: currentIndex };
        bumpBulkSelect();
    };

    const selectAll = (resourceId) => {
        const rows = sortedTableDataRef.current[resourceId] || mappings[resourceId] || [];
        bulkSelectRef.current = { ...bulkSelectRef.current, [resourceId]: new Set(rows.map(r => r.mapping_id)) };
        bumpBulkSelect();
    };

    const deselectAll = (resourceId) => {
        bulkSelectRef.current = { ...bulkSelectRef.current, [resourceId]: new Set() };
        bumpBulkSelect();
    };

    // Bumped to remount the bulk CompositeValueSelect, which keeps its own selection state.
    const [bulkFormKey, setBulkFormKey] = useState(0);
    const resetBulkForm = () => {
        actionReset({ bulkStatus: '', bulkDestinationId: '', bulkNotes: '' });
        setBulkFormKey(k => k + 1);
    };

    const handleBulkUpdate = async (formData, resourceId) => {
        const selectedIds = [...getBulkSet(resourceId)];
        if (selectedIds.length === 0) return ToastBuilder('error', 'No rows selected');

        const body = { mappingIds: selectedIds };
        if (formData.bulkStatus) body.status = formData.bulkStatus;
        if (formData.bulkDestinationId === 'unset') body.destination_id = -1;
        else if (formData.bulkDestinationId && formData.bulkDestinationId !== '') body.destination_id = parseInt(formData.bulkDestinationId);
        if (formData.bulkNotes && formData.bulkNotes.trim()) body.notes = formData.bulkNotes;

        if (Object.keys(body).length === 1) return ToastBuilder('error', 'Nothing to change — pick a status, destination, or notes');

        if (body.status === 'Delete') {
            const confirmed = await confirm(`Remove ${selectedIds.length} composite mapping(s)? They will be excluded from migrations. Destinations and notes are kept.`, {
                title: 'Remove Composite Mappings',
                confirmLabel: 'Remove',
                cancelLabel: 'Cancel',
            });
            if (!confirmed) return;
        }

        const res = await ApiRequest('PATCH', '/dmt/jobs/{jobId}/compositeMappings/bulkUpdate', {
            pathParams: { jobId },
            body,
        });

        if (res.success && res.data?.success !== false) {
            ToastBuilder('success', `Updated ${res.data?.updated || selectedIds.length} mapping(s)`);
            deselectAll(resourceId);
            fetchGroupData(resourceId);
            resetBulkForm();
        } else {
            ToastBuilder('error', res.data?.message || res.error || 'Failed to bulk update');
        }
    };

    // DMT Action wrapper for components that expect handleDMTAction pattern
    const handleDMTAction = async (actionName, sourceResourceId, callback, formData) => {
        const res = await ApiRequest('POST', '/dmt/jobs/{jobId}/actions/{actionName}', {
            pathParams: { jobId, actionName },
            body: { sourceResourceId, formData },
        });
        if (callback) callback(res.success ? res : { data: res.data });
    };

    const handleLoadMappings = async (formData, sourceResourceId) => {

        
        const raw = formData['loadMappingsInput' + sourceResourceId] || '';
        const allowOverwrite = parseInt(formData['loadMappingsOverwrite' + sourceResourceId]) === 1;
        if (!raw.trim()) return ToastBuilder('error', 'No mappings to load');

        const groupRows = mappings[sourceResourceId] || [];
        const lines = raw.trim().split('\n');
        let updated = 0;
        let skipped = [];

        for (const line of lines) {
            const [sourcePart, destPart] = line.split('\t').map(s => (s || '').trim());
            if (!sourcePart || !destPart) { skipped.push(sourcePart || '(empty)'); continue; }

            // Find source mapping by matching source_path
            const sourceMatch = groupRows.find(m =>
                (m.source_path || '').toLowerCase() === sourcePart.toLowerCase()
                || (m.item_name || m.subtype_name || '').toLowerCase() === sourcePart.toLowerCase()
            );

            if (!sourceMatch) { skipped.push(sourcePart); continue; }
            if (!allowOverwrite && sourceMatch.destination_id > 0) { skipped.push(sourcePart + ' (already mapped)'); continue; }

            // Find destination by matching path label
            const destMatch = destLeaves.find(l =>
                l.pathLabel.toLowerCase() === destPart.toLowerCase()
                || l.value?.toLowerCase() === destPart.toLowerCase()
            );

            if (!destMatch) { skipped.push(`${sourcePart} → ${destPart} (dest not found)`); continue; }

            const destId = destMatch.resource_value_id || destMatch.value_id;
            await ApiRequest('PATCH', '/dmt/jobs/{jobId}/compositeMappings/{mappingId}', {
                pathParams: { jobId, mappingId: String(sourceMatch.mapping_id) },
                body: { destination_id: destId, status: 'Pending Approval' },
            });
            updated++;
        }

        if (updated > 0) ToastBuilder('success', `${updated} mapping(s) loaded`);
        if (skipped.length > 0) ToastBuilder('warning', `${skipped.length} skipped: ${skipped.slice(0, 5).join(', ')}${skipped.length > 5 ? '...' : ''}`);
        fetchGroupData(sourceResourceId);
    };

    if (!compositeEntries.length) return null;

    return (
        <Row>
            <ConfirmModal />
            <Container>
                <Card>
                    <CardHeader>
                        <Row>
                            <Col sm='6'><h4>Composite Value Mapping</h4></Col>
                        </Row>
                    </CardHeader>
                    <CardBody>
                        <div className="default-according" id="accordion">
                            {compositeEntries.map((entry) => {
                                const sourceResourceId = entry.source_resource_id;
                                const accordionId = `composite-${sourceResourceId}`;
                                const isExpanded = isOpen === accordionId;
                                const groupRows = mappings[sourceResourceId] || [];
                                const groupApproved = groupRows.filter(m => m.status === 'Approved').length;
                                const groupMapped = groupRows.filter(m => m.destination_id > 0).length;
                                const groupTotal = groupRows.length;
                                const isGroupLoaded = loadedGroups[sourceResourceId];

                                // Determine source_type for summary lookup
                                const entryOptions = typeof entry.mapping_options === 'string' ? JSON.parse(entry.mapping_options || '{}') : (entry.mapping_options || {});
                                const sourceType = entryOptions.source_chain?.type === 'association_table' ? 'association' : 'resource_value';
                                const summaryData = summary[sourceType];

                                const groupTableData = [...groupRows].sort((a, b) => {
                                    // Mapped first, unmapped last
                                    const aHasDest = a.destination_id > 0;
                                    const bHasDest = b.destination_id > 0;
                                    if (aHasDest && !bHasDest) return -1;
                                    if (!aHasDest && bHasDest) return 1;
                                    // Then by recency
                                    const aUpdated = a.updated_at || 0;
                                    const bUpdated = b.updated_at || 0;
                                    if (aUpdated !== bUpdated) return bUpdated - aUpdated;
                                    // Then alphabetical by source path
                                    const aSource = (a.source_path || '').toLowerCase();
                                    const bSource = (b.source_path || '').toLowerCase();
                                    return aSource.localeCompare(bSource);
                                });

                                const inactiveStatuses = INACTIVE_STATUSES;
                                const inactiveCount = groupTableData.filter(m => inactiveStatuses.includes(m.status)).length;

                                // Overloads are computed over the whole group, so filters never hide a conflict's counterpart.
                                const conflictIndex = buildConflictIndex(groupTableData);
                                const rowConflicts = {};
                                for (const r of groupTableData) {
                                    if (inactiveStatuses.includes(r.status)) continue;
                                    const c = getConflicts(conflictIndex, r, r.destination_id);
                                    if (c.length) rowConflicts[r.mapping_id] = c;
                                }
                                const conflictCount = Object.keys(rowConflicts).length;

                                // Tags are built over every group (not the filtered view) so codes don't shift when filtering.
                                const conflictGroups = getConflictGroups(conflictIndex).map(([key, rows]) => ({
                                    key,
                                    boardKey: String(rows[0].board_id ?? rows[0].board_name),
                                    destinationLabel: rows[0].destination_path || rows[0].destination_value || `#${rows[0].destination_id}`,
                                    contextLabel: getLeafContext(rows[0]).label,
                                    leaves: rows.map(r => getLeafContext(r).leaf),
                                    mappingIds: new Set(rows.map(r => r.mapping_id)),
                                })).sort((a, b) => a.destinationLabel.localeCompare(b.destinationLabel) || a.contextLabel.localeCompare(b.contextLabel));
                                const conflictTags = buildTagMap(conflictGroups.map(g => g.key));
                                const conflictGroupsByKey = Object.fromEntries(conflictGroups.map(g => [g.key, g]));
                                const activeTagGroup = tagFilter ? conflictGroupsByKey[tagFilter] : null;
                                const legendGroups = boardFilter === 'all' ? conflictGroups : conflictGroups.filter(g => g.boardKey === boardFilter);
                                const toggleTagFilter = (key) => setTagFilter(tagFilter === key ? null : key);

                                const boardOptions = [...new Map(groupTableData.filter(m => m.board_name).map(m => [String(m.board_id ?? m.board_name), m.board_name])).entries()]
                                    .sort((a, b) => a[1].localeCompare(b[1]));

                                const statusFiltered = mappingFilter === 'all' ? groupTableData
                                    : mappingFilter === 'inactive' ? groupTableData.filter(m => inactiveStatuses.includes(m.status))
                                    : groupTableData.filter(m => !inactiveStatuses.includes(m.status));
                                const levelFiltered = levelFilter === 'all' ? statusFiltered
                                    : levelFilter === 'type' ? statusFiltered.filter(m => !m.subtype_name && !m.item_name)
                                    : levelFilter === 'subtype' ? statusFiltered.filter(m => m.subtype_name && !m.item_name)
                                    : statusFiltered.filter(m => !!m.item_name);
                                const boardFiltered = boardFilter === 'all' ? levelFiltered
                                    : levelFiltered.filter(m => String(m.board_id ?? m.board_name) === boardFilter);
                                const conflictFiltered = conflictsOnly ? boardFiltered.filter(m => rowConflicts[m.mapping_id]) : boardFiltered;
                                // A tag filter shows the whole group regardless of the other filters, so no member is hidden.
                                const preTagFilter = activeTagGroup ? groupTableData.filter(m => activeTagGroup.mappingIds.has(m.mapping_id)) : conflictFiltered;
                                // Expose each row's overload tag code as a searchable field so the table search can match on it.
                                const filteredTableData = preTagFilter.map(m => {
                                    const gk = rowConflicts[m.mapping_id] ? getGroupKey(m, m.destination_id) : null;
                                    return gk && conflictTags[gk] ? { ...m, overload_tag: conflictTags[gk].code } : m;
                                });

                                const handleAccordionToggle = () => {
                                    if (isExpanded) {
                                        setIsOpen(null);
                                        return;
                                    }
                                    setIsOpen(accordionId);
                                    if (!isGroupLoaded) fetchGroupData(sourceResourceId);
                                };

                                return (
                                    <Card key={accordionId}>
                                        <CardHeader className='dmt-accordion-section'>
                                            <center>
                                                <Btn attrBtn={{ as: Card.Header, className: `dmt-accordion-header ${isExpanded ? 'dmt-accordion-open' : ''}`, color: 'default', onClick: handleAccordionToggle }}>
                                                    <span title={`Mapped: ${groupMapped}, Unmapped: ${groupTotal - groupMapped}, Total: ${groupTotal}`}>
                                                        {entry.source_resource_name} <ArrowRight size={14} style={{ verticalAlign: 'middle', margin: '0 4px' }} /> {entry.destination_resource_name} {isGroupLoaded ? `(Approved - ${groupApproved} / ${groupTotal})` : summaryData ? `(Approved - ${summaryData.approved_count} / ${summaryData.total_count})` : ''}
                                                        <div className='text-muted' style={{ fontSize: '10px', lineHeight: 1.2 }}>{sourceResourceId} – {entry.destination_resource_id} (Composite)</div>
                                                    </span>
                                                </Btn>
                                            </center>
                                        </CardHeader>
                                        <Collapse isOpen={isExpanded}>
                                            <CardBody>
                            {reloadBanner === sourceResourceId && (
                                <div className='alert alert-warning d-flex align-items-center justify-content-between py-2 px-3 mb-2' style={{ fontSize: '13px' }}>
                                    <span>⏳ Reload in progress — click <strong>Refresh</strong> when ready to see updated data.</span>
                                    <ButtonBuilder label='Refresh Now' className='btn-info btn-sm m-0 py-1 px-2' onClick={() => { fetchGroupData(sourceResourceId); setReloadBanner(null); }} />
                                </div>
                            )}
                            {!loading && (
                                <OverloadLegend groups={legendGroups} tags={conflictTags} activeKey={tagFilter} onSelect={setTagFilter} />
                            )}
                            {!loading && activeTagGroup && (
                                <div className='d-flex align-items-center mb-2' style={{ gap: 8, fontSize: 13 }}>
                                    <span className='text-muted'>Filtered to</span>
                                    <ConflictTag tag={conflictTags[tagFilter]} active />
                                    <span>{activeTagGroup.destinationLabel} <span className='text-muted'>in {activeTagGroup.contextLabel}</span></span>
                                    <ButtonBuilder label='✕ Clear' type='button' className='btn-outline-secondary btn-sm py-0 px-2 m-0' useSpinner={false} onClick={() => setTagFilter(null)} />
                                </div>
                            )}
                            {loading ? <ProgressLoader /> : (
                                <TableBuilder
                                    subHeader={true}
                                    columnsToSearch={['source_path', 'destination_path', 'destination_value', 'destination_parent_name', 'status', 'board_name', 'type_name', 'subtype_name', 'item_name', 'overload_tag']}
                                    data={filteredTableData}
                                    sortField={null}
                                    pagination={true}
                                    paginationPerPage={25}
                                    paginationRowsPerPageOptions={[25, 50, 75, 100]}
                                    processing={loading}
                                    onSortChange={(sorted) => { sortedTableDataRef.current = { ...sortedTableDataRef.current, [sourceResourceId]: sorted }; }}
                                    styles={{ cells: { style: { paddingTop: '5px', paddingBottom: '5px' } } }}
                                    title={<><div className='d-flex' style={{ gap: '5px' }}>
                                        {(mappingFilter !== 'active' || inactiveCount > 0) &&
                                        <ButtonBuilder label={
                                            mappingFilter === 'active' ? 'Show All'
                                            : mappingFilter === 'all' ? `Viewing: All ${groupTableData.length}`
                                            : `Viewing: ${inactiveCount} Inactive`
                                        } className={`p-2 px-2 ${mappingFilter === 'active' ? 'btn-primary' : 'btn-info'}`}
                                            style={{ minWidth: '150px' }} useSpinner={false} color={null}
                                            onClick={() => setMappingFilter(mappingFilter === 'active' ? 'all' : mappingFilter === 'all' ? 'inactive' : 'active')}
                                        />}
                                        <BulkEditControls
                                            bulkEditMode={bulkEditMode}
                                            onToggle={() => { setBulkEditMode(!bulkEditMode); if (bulkEditMode) deselectAll(sourceResourceId); }}
                                            selectedCount={getBulkCount(sourceResourceId)}
                                            totalCount={groupTableData.length}
                                            onSelectAll={() => selectAll(sourceResourceId)}
                                            onDeselectAll={() => deselectAll(sourceResourceId)}
                                            editModal={
                                                <ModalButtonBuilder className='btn-danger m-0'
                                                    title={`Edit (${getBulkCount(sourceResourceId)})`}
                                                    disabled={getBulkCount(sourceResourceId) === 0}
                                                    buttonStyle={{ height: '38px', borderRadius: 0, ...(getBulkCount(sourceResourceId) === 0 ? { cursor: 'not-allowed' } : {}) }}
                                                    modalTitle={`Bulk Edit ${entry.source_resource_name} mappings`}
                                                    style={{ maxWidth: 750, width: '90vw' }}
                                                    formId={'bulk-update-composite-form-' + sourceResourceId}
                                                    body={
                                                    <Form id={'bulk-update-composite-form-' + sourceResourceId} onSubmit={actionHandleSubmit((formData) => handleBulkUpdate(formData, sourceResourceId))}>
                                                        <input type='hidden' {...actionRegister('bulkDestinationId')} />
                                                        <p>Updating <strong>{getBulkCount(sourceResourceId)}</strong> selected {entry.source_resource_name} mapping(s).</p>
                                                        <p style={{fontSize: 'small', color: 'grey'}}>Fields left as "Do not change" or blank keep their current value.</p>
                                                        <ButtonBuilder label='Clear' type='button' className='btn-outline-secondary btn-sm mt-2 mb-2 me-2' useSpinner={false}
                                                            onClick={resetBulkForm}
                                                        />
                                                        <ModalButtonBuilder title='Show Selected' type='button'
                                                            className='btn-hover-info btn-sm mt-2 mb-2'
                                                            modalTitle={`Selected Mappings (${getBulkCount(sourceResourceId)})`}
                                                            style={{ width: 700, maxWidth: 700 }}
                                                            onSubmit={false} onCancelLabel='Close'
                                                            body={
                                                                <ul style={{ fontSize: 'small', paddingLeft: 20 }}>
                                                                    {groupTableData.filter(r => getBulkSet(sourceResourceId).has(r.mapping_id)).map(r => (
                                                                        <li key={r.mapping_id}>
                                                                            {r.source_path}
                                                                            <span className='text-muted'> → {r.destination_path || 'unmapped'}</span>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            }
                                                        />
                                                        <RegisteredInputsBuilder
                                                            registrator={actionRegister}
                                                            registratorErrors={actionErrors}
                                                            control={actionControl}
                                                            inputs={[
                                                                {
                                                                    label: 'Status', labelSize: 3, inputSize: 9, size: 12, type: 'reactselect',
                                                                    name: 'bulkStatus',
                                                                    helpText: 'Delete removes the mappings from migrations (asks to confirm); destinations and notes are kept.',
                                                                    options: [{ label: 'Do not change', value: '' }, ...buildSelectOptionsFromArray(['Approved', 'Pending Approval', 'Under Review', 'Dev Review', 'Ignore', 'Delete'], { labelIsValue: true })],
                                                                },
                                                            ]}
                                                        />
                                                        <div className='row mb-3'>
                                                            <label className='col-3 col-form-label'>Destination</label>
                                                            <div className='col-9'>
                                                                {bulkDestinationId === 'unset' ? (
                                                                    <div className='d-flex align-items-center justify-content-between p-2 mb-1' style={{ border: '1px solid #e9ecef', borderRadius: 6, background: '#f8f9fa', fontSize: 13 }}>
                                                                        <span className='txt-warning'>Destination will be cleared (unmapped) for all selected rows.</span>
                                                                        <ButtonBuilder label='Undo' type='button' className='btn-outline-secondary btn-sm py-1 px-2 m-0' useSpinner={false}
                                                                            onClick={() => actionSetValue('bulkDestinationId', '')}
                                                                        />
                                                                    </div>
                                                                ) : <>
                                                                    <CompositeValueSelect
                                                                        key={'bulk-dest-' + sourceResourceId + '-' + bulkFormKey}
                                                                        jobId={jobId}
                                                                        sourceResourceId={sourceResourceId}
                                                                        value={null}
                                                                        onChange={(val) => actionSetValue('bulkDestinationId', val ? String(val) : '')}
                                                                        chainData={destChainData}
                                                                    />
                                                                    <small className='text-muted d-block mt-1'>Leave unselected to keep current destinations. Select any level to update.</small>
                                                                    {(() => {
                                                                        if (!bulkDestinationId || bulkDestinationId === 'unset') return null;
                                                                        // Simulate the bulk assignment, then list contexts that would end up with more than one leaf.
                                                                        const selected = getBulkSet(sourceResourceId);
                                                                        const overrides = {};
                                                                        selected.forEach(id => { overrides[id] = bulkDestinationId; });
                                                                        const simulated = buildConflictIndex(groupTableData, overrides);
                                                                        const overloaded = Object.entries(simulated)
                                                                            .filter(([k, rows]) => k.startsWith(`${bulkDestinationId}|`) && rows.length > 1 && rows.some(r => selected.has(r.mapping_id)));
                                                                        if (!overloaded.length) return null;
                                                                        return <div className='p-2 mt-2' style={{ border: '1px solid #ffe8a1', borderRadius: 6, background: '#fff8e1', fontSize: 13 }}>
                                                                            <div className='txt-warning' style={{ fontWeight: 600 }}>This will overload the destination in {overloaded.length} board/type context(s)</div>
                                                                            <ul className='mb-1' style={{ paddingLeft: 20 }}>
                                                                                {overloaded.map(([k, rows]) => (
                                                                                    <li key={k} className='mb-1'>
                                                                                        <ConflictTag tag={conflictTags[k] || buildTagMap([k])[k]} />{' '}
                                                                                        {getLeafContext(rows[0]).label}: {rows.map(r => getLeafContext(r).leaf).join(', ')}
                                                                                        {!conflictTags[k] ? <span className='text-muted'> (new)</span> : null}
                                                                                    </li>
                                                                                ))}
                                                                            </ul>
                                                                            <small className='text-muted'>Reverse sync can't tell these leaves apart. Different boards/types are fine.</small>
                                                                        </div>;
                                                                    })()}
                                                                    <ButtonBuilder
                                                                        label='Unset (clear value)'
                                                                        className='btn-outline-secondary py-1 px-2 mt-1'
                                                                        style={{ fontSize: '11px' }}
                                                                        useSpinner={false}
                                                                        type='button'
                                                                        onClick={() => actionSetValue('bulkDestinationId', 'unset')}
                                                                    />
                                                                </>}
                                                            </div>
                                                        </div>
                                                        <div className='mb-2'>
                                                            <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, display: 'block' }}>Notes</label>
                                                            <textarea
                                                                className='form-control'
                                                                rows={3}
                                                                placeholder='Leave blank to keep current notes. Text here replaces notes on every selected row.'
                                                                style={{ resize: 'vertical', minHeight: 70 }}
                                                                {...actionRegister('bulkNotes')}
                                                            />
                                                        </div>
                                                    </Form>
                                                }
                                            />
                                            }
                                        />
                                    </div></>}
                                    actions={<Fragment>
                                        {boardOptions.length > 1 && (
                                            <select
                                                className='form-select form-select-sm m-1'
                                                style={{ width: '200px', height: '38px', display: 'inline-block' }}
                                                value={boardFilter}
                                                onChange={(e) => setBoardFilter(e.target.value)}
                                                aria-label='Filter by board'
                                            >
                                                <option value='all'>All Boards</option>
                                                {boardOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                                            </select>
                                        )}
                                        <select
                                            className='form-select form-select-sm m-1'
                                            style={{ width: '130px', height: '38px', display: 'inline-block' }}
                                            value={levelFilter}
                                            onChange={(e) => setLevelFilter(e.target.value)}
                                        >
                                            <option value='all'>All Levels</option>
                                            <option value='type'>Type Only</option>
                                            <option value='subtype'>SubType</option>
                                            <option value='item'>Item</option>
                                        </select>
                                        {(conflictCount > 0 || conflictsOnly) && (
                                            <ButtonBuilder
                                                label={conflictsOnly ? `Showing ${conflictCount} Overloaded` : `Overloaded (${conflictCount})`}
                                                className={`m-1 py-2 ${conflictsOnly ? 'btn-warning' : 'btn-outline-warning'}`}
                                                useSpinner={false}
                                                type='button'
                                                onClick={() => setConflictsOnly(!conflictsOnly)}
                                            />
                                        )}
                                        <MapByLeafModal
                                            jobId={jobId}
                                            sourceResourceId={sourceResourceId}
                                            rows={groupTableData}
                                            destChainData={destChainData}
                                            leafIdentity={entryOptions.source_chain?.leaf_identity}
                                            onDone={() => fetchGroupData(sourceResourceId)}
                                        />
                                        <ButtonBuilder label='Refresh' className='btn-info m-1 py-2' title='Re-draw this table from saved data. No API call.' onClick={() => fetchGroupData(sourceResourceId)} />
                                        <VerticalButtonGroup label='Reload from API' className='btn-secondary m-1 py-2'
                                            style={{ minWidth: 175 }}
                                            buttons={[
                                                <ButtonBuilder
                                                    label='Associations only'
                                                    className='btn-outline-secondary'
                                                    useSpinner={false}
                                                    title='Re-imports TSI associations from CWM for this board scope. Makes API calls.'
                                                    onClick={() => repullFromApi(sourceResourceId, 'reload-tsi')}
                                                />,
                                                <ButtonBuilder
                                                    label='Associations + values'
                                                    className='btn-outline-secondary'
                                                    useSpinner={false}
                                                    title="Re-imports both chains' values and the associations. Makes API calls and may take a minute."
                                                    onClick={() => repullFromApi(sourceResourceId, 'reload-composite-dependencies')}
                                                />,
                                            ]}
                                        />
                                        {importLog.length > 0 && (
                                            <ModalButtonBuilder title={`Log (${importLog.length})`} className='btn-warning m-1 py-2'
                                                onSubmit={false} onCancelLabel='Close'
                                                modalTitle='Import Log'
                                                style={{ minWidth: '600px' }}
                                                body={
                                                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                                        {importLog.map((entry, idx) => (
                                                            <div key={idx} className='mb-3 p-2 border rounded' style={{ fontSize: '12px' }}>
                                                                <div className='d-flex justify-content-between mb-1'>
                                                                    <strong>{entry.message}</strong>
                                                                    <small className='text-muted'>{moment(entry.timestamp).fromNow()}</small>
                                                                </div>
                                                                {entry.skipped && entry.skipped.length > 0 && (
                                                                    <>
                                                                        <div className='d-flex justify-content-end mb-1'>
                                                                            <ButtonBuilder
                                                                                label='Copy Failed Rows'
                                                                                className='btn-outline-secondary py-1 px-2'
                                                                                style={{ fontSize: '11px' }}
                                                                                useSpinner={false}
                                                                                onClick={() => {
                                                                                    const rawRows = entry.rawRows || [];
                                                                                    const failedLines = entry.skipped.map(s => {
                                                                                        const rowIdx = (s.row || 1) - 1;
                                                                                        return rawRows[rowIdx] ? rawRows[rowIdx].join('\t') : `Row ${s.row}\t${s.reason}`;
                                                                                    });
                                                                                    handleCopy(failedLines.join('\n'));
                                                                                    ToastBuilder('success', 'Failed rows copied to clipboard');
                                                                                }}
                                                                            />
                                                                        </div>
                                                                        <table className='table table-sm mb-0' style={{ fontSize: '11px' }}>
                                                                            <thead><tr><th style={{width: 50}}>Row</th><th>Reason</th></tr></thead>
                                                                            <tbody>
                                                                                {entry.skipped.map((s, i) => (
                                                                                    <tr key={i}><td>{s.row}</td><td>{s.reason}</td></tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                }
                                            />
                                        )}
                                        <VerticalButtonGroup label='Actions' className='btn-danger m-1 py-2'
                                            style={{ minWidth: '140px' }}
                                            buttons={[
                                                <ModalButtonBuilder title='Quick Approve' className='btn-outline-danger'
                                                    formId={'quick-approve-composite-form-' + sourceResourceId}
                                                    body={
                                                        <Form id={'quick-approve-composite-form-' + sourceResourceId} onSubmit={handleSubmit(() => handleQuickApprove(sourceResourceId))}>
                                                            <p>Set status to "Approved" for all mapped composite mappings that are not yet approved.</p>
                                                        </Form>
                                                    }
                                                />,
                                                <ModalButtonBuilder title='Load Mappings' className='btn-outline-danger'
                                                    formId={'load-composite-mappings-form-' + sourceResourceId}
                                                    modalTitle={`Load ${entry.source_resource_name} Mappings`}
                                                    onClick={() => { actionSetValue('loadMappingsInput' + sourceResourceId, ''); actionSetValue('loadMappingsOverwrite' + sourceResourceId, 0); }}
                                                    body={
                                                        <Form id={'load-composite-mappings-form-' + sourceResourceId} onSubmit={actionHandleSubmit((formData) => handleLoadMappings(formData, sourceResourceId))}>
                                                            <p>Paste tab-separated mappings: <b>Source Path → Destination Path</b></p>
                                                            <ul style={{ fontSize: '12px', color: '#636E72' }}>
                                                                <li>Source can be the full path or just the leaf value name</li>
                                                                <li>Destination can be the full path or leaf value name</li>
                                                                <li>Unrecognized values will be skipped</li>
                                                            </ul>
                                                            <RegisteredInputsBuilder
                                                                registrator={actionRegister}
                                                                registratorErrors={actionErrors}
                                                                control={actionControl}
                                                                inputs={[
                                                                    {
                                                                        label: 'Overwrite', offset: 1, labelSize: 2, type: 'reactselect',
                                                                        name: 'loadMappingsOverwrite' + sourceResourceId,
                                                                        options: [{ label: 'Only update unmapped rows', value: 0 }, { label: 'Allow overwriting existing', value: 1 }],
                                                                    },
                                                                    {
                                                                        label: 'Mappings', offset: 1, inputSize: 10, type: 'textarea',
                                                                        name: 'loadMappingsInput' + sourceResourceId,
                                                                        placeholder: 'Incident > Security > 1 - MFA Tools\tSecurity\n1 - Password Reset\tAccount & Access > Login / Password Reset',
                                                                        style: { height: '200px', fontFamily: 'monospace', fontSize: '12px' },
                                                                        settings: { handleTabs: true, resize: true },
                                                                    },
                                                                ]}
                                                            />
                                                        </Form>
                                                    }
                                                />,
                                                <ImportAssociationMappingsModal
                                                    sourceResourceId={sourceResourceId}
                                                    sourceResourceName={entry.source_resource_name}
                                                    actionRegister={actionRegister}
                                                    actionErrors={actionErrors}
                                                    actionControl={actionControl}
                                                    actionSetValue={actionSetValue}
                                                    actionReset={actionReset}
                                                    handleDMTAction={handleDMTAction}
                                                    getResourceValueMappings={() => fetchGroupData(sourceResourceId)}
                                                    onImportResult={(result) => setImportLog(prev => [{ ...result, timestamp: Date.now(), sourceResourceId }, ...prev])}
                                                    lastImportSettings={lastImportSettings}
                                                    setLastImportSettings={setLastImportSettings}
                                                />,
                                            ]}
                                        />
                                    </Fragment>}
                                    columns={[
                                        {
                                            name: '',
                                            selector: (row) => <CheckBox className="medium-checkbox-cursor-pointer"
                                                id={`composite-checkbox-${row.mapping_id}`}
                                                reference={bulkSelectRef.current[sourceResourceId] || new Set()}
                                                checked={getBulkSet(sourceResourceId).has(row.mapping_id)}
                                                onChange={(e) => handleBulkCheckbox(e, row.mapping_id, sourceResourceId)}
                                            />,
                                            sortable: false,
                                            center: true,
                                            width: '70px',
                                            grow: 0,
                                            omit: !bulkEditMode,
                                        },
                                        {
                                            name: 'Source (CWM)',
                                            selector: (row) => <LabelWithSubtext
                                                text={row.item_name || row.subtype_name || row.type_name}
                                                subtext={row.source_type === 'association'
                                                    ? row.item_name
                                                        ? `${row.board_name} > ${row.type_name} > ${row.subtype_name}`
                                                        : row.subtype_name
                                                            ? `${row.board_name} > ${row.type_name}`
                                                            : row.board_name || ''
                                                    : row.board_name || ''
                                                }
                                                align='center'
                                            />,
                                            sortable: true,
                                            center: true,
                                            sortFunction: (a, b) => sortByKey(a, b, 'source_path'),
                                        },
                                        {
                                            name: 'Destination (FS)',
                                            selector: (row) => {
                                                if (!row.destination_value) return '-';
                                                // Build subtext from destination_path, excluding the leaf value itself
                                                let subtext = '';
                                                if (row.destination_path) {
                                                    const parts = row.destination_path.split(' > ');
                                                    if (parts.length > 1) {
                                                        subtext = parts.slice(0, -1).join(' > ');
                                                    }
                                                } else {
                                                    subtext = row.destination_parent_name || '';
                                                }
                                                const groupKey = rowConflicts[row.mapping_id] ? getGroupKey(row, row.destination_id) : null;
                                                return <div className='d-flex flex-column align-items-center' style={{ gap: 2 }}>
                                                    <LabelWithSubtext
                                                        text={row.destination_value}
                                                        subtext={subtext}
                                                        align='center'
                                                    />
                                                    {groupKey ? <ConflictTagWithInfo
                                                        tag={conflictTags[groupKey]}
                                                        group={conflictGroupsByKey[groupKey]}
                                                        active={tagFilter === groupKey}
                                                        onToggle={() => toggleTagFilter(groupKey)}
                                                    /> : null}
                                                </div>;
                                            },
                                            sortable: true,
                                            center: true,
                                            sortFunction: (a, b) => sortByKey(a, b, 'destination_value'),
                                        },
                                        {
                                            name: 'Status',
                                            selector: (row) => row.status || '-',
                                            sortable: true,
                                            center: true,
                                            maxWidth: '13%',
                                        },
                                        {
                                            name: 'Notes',
                                            selector: (row) => row.notes ? <HoverOver content={row.notes} text={'VIEW'} /> : '',
                                            sortable: false,
                                            center: true,
                                            maxWidth: '7%',
                                        },
                                        {
                                            name: 'Updated',
                                            selector: (row) => row.updated_at ? moment.utc(row.updated_at * 1000).fromNow() : '-',
                                            sortable: true,
                                            center: true,
                                            maxWidth: '11%',
                                            sortFunction: (a, b) => (a.updated_at || 0) - (b.updated_at || 0),
                                        },
                                        {
                                            name: 'Manage',
                                            selector: (row) => (
                                                <ModalButtonBuilder className='btn-outline-secondary'
                                                    buttonStyle={{ width: 90 }}
                                                    title='Edit'
                                                    modalTitle={`Edit Composite Mapping — ${getSourceLevel(row)}`}
                                                    onClick={() => {
                                                        setValue('destinationResourceValueId' + row.mapping_id, row.destination_id > 0 ? row.destination_id : '');
                                                        setValue('status' + row.mapping_id, row.status || 'Pending Approval');
                                                        setValue('notes' + row.mapping_id, row.notes || '');
                                                    }}
                                                    onSubmit='Submit'
                                                    onCancelLabel='Cancel'
                                                    onDelete={() => handleDeleteMapping(row, sourceResourceId)}
                                                    onDeleteLabel='Delete'
                                                    style={{ maxWidth: 750, width: '90vw' }}
                                                    formId={'edit-assoc-mapping-form' + row.mapping_id}
                                                    body={
                                                        <Form id={'edit-assoc-mapping-form' + row.mapping_id} onSubmit={handleSubmit(data => handleMappingUpdate(data, row.mapping_id, row))}>
                                                            <input type='hidden' {...register('destinationResourceValueId' + row.mapping_id)} />

                                                            <div className='mb-3 p-3' style={{ border: '1px solid #e9ecef', borderRadius: 6, background: '#f8f9fa' }}>
                                                                <div className='d-flex justify-content-between align-items-center mb-1'>
                                                                    <span style={{ fontSize: 13, fontWeight: 600 }}>Source (CWM)</span>
                                                                    <HoverOver
                                                                        content={`Mapping ID: ${row.mapping_id}\n${row.source_type === 'association' ? 'Association' : 'Resource Value'} ID: ${row.source_id}`}
                                                                        text={<span className='badge badge-light'>{getSourceLevel(row)}</span>}
                                                                    />
                                                                </div>
                                                                {row.source_type === 'association' ? <>
                                                                    <SourceDetail label='Board' name={row.board_name} id={row.board_id} />
                                                                    <SourceDetail label='Type' name={row.type_name} id={row.type_value_id} />
                                                                    {row.subtype_name ? <SourceDetail label='SubType' name={row.subtype_name} id={row.subtype_value_id} /> : null}
                                                                    {row.item_name ? <SourceDetail label='Item' name={row.item_name} id={row.item_value_id} /> : null}
                                                                </> : <SourceDetail label='Value' name={row.source_path} id={row.item_value_id} />}
                                                                <small className='text-muted d-block mt-1'>The board/type/subtype/item combination coming from the source system.</small>
                                                            </div>

                                                            <div className='mb-3'>
                                                                <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, display: 'block' }}>Destination</label>
                                                                {row.destination_path ? (
                                                                    <div className='mb-2' style={{ fontSize: 13 }}>
                                                                        <span className='text-muted'>Current: </span>{row.destination_path}
                                                                    </div>
                                                                ) : (
                                                                    <div className='mb-2 txt-warning' style={{ fontSize: 13 }}>Currently unmapped</div>
                                                                )}
                                                                <CompositeValueSelect
                                                                    jobId={jobId}
                                                                    sourceResourceId={sourceResourceId}
                                                                    value={row.destination_id > 0 ? row.destination_id : null}
                                                                    onChange={(val) => setValue('destinationResourceValueId' + row.mapping_id, val, { shouldDirty: true })}
                                                                    chainData={destChainData}
                                                                    defaultMode='flat'
                                                                    compact
                                                                />
                                                                <small className='text-muted d-block mt-1'>The destination chain this combination maps to. Pick at any level; parents are filled in automatically.</small>
                                                                {(() => {
                                                                        // Live check against the picked (unsaved) destination.
                                                                        const picked = watch('destinationResourceValueId' + row.mapping_id);
                                                                        const destId = picked !== undefined && picked !== '' ? picked : row.destination_id;
                                                                        const conflicts = getConflicts(conflictIndex, row, destId);
                                                                        const sameDest = (groupTableData || []).filter(r => r.mapping_id !== row.mapping_id && String(r.destination_id) === String(destId) && !INACTIVE_STATUSES.includes(r.status));
                                                                        const otherContexts = sameDest.length - conflicts.length;
                                                                        return <div className='mt-2'>
                                                                            <OverloadWarning
                                                                                row={row}
                                                                                conflicts={conflicts}
                                                                                destLabel={String(destId) === String(row.destination_id) ? row.destination_value : null}
                                                                                tag={conflicts.length ? (conflictTags[getGroupKey(row, destId)] || buildTagMap([getGroupKey(row, destId)])[getGroupKey(row, destId)]) : null}
                                                                            />
                                                                            {otherContexts > 0 ? (
                                                                                <HoverOver
                                                                                    content={sameDest.filter(r => !conflicts.includes(r)).map(r => `${getLeafContext(r).leaf} (${getLeafContext(r).label})`).join('\n')}
                                                                                    text={<small className='text-muted'>Also used by {otherContexts} mapping(s) in other boards/types — OK, these stay 1:1.</small>}
                                                                                    contentStyle={{ maxWidth: 400 }}
                                                                                />
                                                                            ) : null}
                                                                        </div>;
                                                                    })()}
                                                            </div>
                                                            <RegisteredInputsBuilder
                                                                control={control}
                                                                registrator={register}
                                                                registratorErrors={errors}
                                                                inputs={[
                                                                    {
                                                                        label: 'Status',
                                                                        labelSize: 3,
                                                                        type: 'reactselect',
                                                                        name: 'status' + row.mapping_id,
                                                                        helpText: 'Approve when the mapping is ready to migrate. Ignore to skip it.',
                                                                        // Keep the row's current status selectable even if it's no longer offered (e.g. Delete).
                                                                        options: buildOptionsFromArray([...new Set(['Approved', 'Pending Approval', 'Under Review', 'Dev Review', 'Ignore', ...(row.status ? [row.status] : [])])], { labelIsValue: true }),
                                                                    },
                                                                ]}
                                                            />
                                                            <div className='mb-2'>
                                                                <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, display: 'block' }}>Notes</label>
                                                                <textarea
                                                                    className='form-control'
                                                                    rows={3}
                                                                    placeholder='Optional notes about this mapping...'
                                                                    style={{ resize: 'vertical', minHeight: 70 }}
                                                                    {...register('notes' + row.mapping_id)}
                                                                />
                                                            </div>
                                                            {row.updated_at ? (
                                                                <small className='text-muted d-block text-end'>
                                                                    Last updated {moment.utc(row.updated_at * 1000).fromNow()} ({moment.utc(row.updated_at * 1000).local().format('MMM D, YYYY h:mm A')})
                                                                </small>
                                                            ) : null}
                                                        </Form>
                                                    }
                                                />
                                            ),
                                            sortable: false,
                                            center: true,
                                            width: '140px',
                                            grow: 0,
                                        },
                                    ]}
                                />
                            )}
                        </CardBody>
                    </Collapse>
                </Card>
                );
            })}
            </div>
            </CardBody>
                </Card>
            </Container>
        </Row>
    );
};

export default AssociationMappingSection;
